import { createStore, useStore } from 'zustand';

import { acceptedOrdersStore } from '@stores/acceptedOrders';
import { authStore } from '@stores/auth';
import { logger } from '@lib/logger';
import { lookupsStore } from '@stores/lookups';
import { FirestoreOrder, Order } from '@lib/types';
import { Unsubscribe, firebase } from '@lib/firebase';

import { fromFirestoreOrderToOrder } from './fromFirestoreOrderToOrder';

interface OrderDetailsStore {
  loading: boolean;
  error: Error | null;
  order: Order | null;
  rejectedReason: null;
  subscribe: (orderId: string) => void;
  unsubscribe: () => void;
  onSnapshotUnsubscribe: Unsubscribe | null;
  accept: (order: Order) => void;
}

export const orderDetailsStore = createStore<OrderDetailsStore>((set, get) => ({
  error: null,
  loading: false,
  order: null,
  onSnapshotUnsubscribe: null,
  rejectedReason: null,
  
  subscribe: async (orderId) => {
    get().unsubscribe();

    set({ loading: true, error: null });

    try {
      const onSnapshotUnsubscribe = await firebase.onQuerySnapshot<FirestoreOrder>({
        collection: 'orders',
        where: [
          ['orderMessage.uuid', '==', orderId],
          lookupsStore.getState().value?.orderQuery ?? null,
        ],
        onNext: (firestoreOrders) => {
          const firestoreOrder = firestoreOrders[0];
          if (!firestoreOrder) {
            set({ loading: false, order: null });
            return;
          }

          const order = fromFirestoreOrderToOrder(firestoreOrder);

          if (order?.orderMessage?.data?.state === 'ACCEPTED') {
            const accepted = acceptedOrdersStore.getState().orders.find(
              (item) => item.uuid === order.orderMessage.uuid
            );
            if (accepted?.tripDriverCache) {
              order.tripDriverCache = accepted.tripDriverCache;
            }
          }

          set({ loading: false, error: null, order });
        },
        onError: (error) => {
          set({ error, loading: false });
          logger.error('Order Sub Error:', error);
        },
      });

      set({ onSnapshotUnsubscribe });
    } catch (e) {
      set({ error: e as Error, loading: false });
    }
  },

  unsubscribe: () => {
    const { onSnapshotUnsubscribe } = get();
    if (onSnapshotUnsubscribe) {
      onSnapshotUnsubscribe();
    }
    set({ onSnapshotUnsubscribe: null, order: null, loading: false, error: null });
  },
  
  accept: () => {},
}));

acceptedOrdersStore.subscribe((state) => {
  const currentOrder = orderDetailsStore.getState().order;
  if (!currentOrder || !state.orders) return;

  const match = state.orders.find(o => o.uuid === currentOrder.orderMessage.uuid);
  
  if (match && match.tripDriverCache !== currentOrder.tripDriverCache) {
    orderDetailsStore.setState({
      order: {
        ...currentOrder,
        tripDriverCache: match.tripDriverCache
      }
    });
  }
});

// clear out orders and unsubscribe on logout
authStore.subscribe((state) => {
  if (orderDetailsStore.getState().order && !state.computed.isLoggedIn) {
    orderDetailsStore.getState().unsubscribe();
  }
});

export const useOrderDetailsStore = () => useStore(orderDetailsStore);
